type Tick = {
  Timestamp: string
  Symbol: string
  Bid: number
  Ask: number
}

type Bar = {
  time: number
  open: number
  high: number
  low: number
  close: number
  volume: number
}

type Subscription = {
  symbol: string
  resolution: string
  onRealtimeCallback: (bar: Bar) => void
}

class DxtradeCandleStream {
  private subscriptions = new Map<string, Subscription>()
  private lastBars = new Map<string, Bar>()

  subscribe(
    subscriberUID: string,
    symbol: string,
    resolution: string,
    onRealtimeCallback: (bar: Bar) => void
  ) {
    this.subscriptions.set(subscriberUID, {
      symbol,
      resolution,
      onRealtimeCallback,
    })
  }

  unsubscribe(subscriberUID: string) {
    this.subscriptions.delete(subscriberUID)
  }

  handleTick(tick: Tick) {
    const price = (tick.Bid + tick.Ask) / 2
    const ts = parseTickTimestamp(tick.Timestamp)

    if (!Number.isFinite(ts)) {
      console.warn('[dxtradeCandleStream] invalid tick timestamp', tick.Timestamp)
      return
    }

    this.subscriptions.forEach((sub) => {
      if (sub.symbol !== tick.Symbol) return

      const key = `${sub.symbol}|${sub.resolution}`
      const barTime = getBarStartTime(ts, sub.resolution)

      let bar = this.lastBars.get(key)

      if (!bar) {
        bar = {
          time: barTime,
          open: price,
          high: price,
          low: price,
          close: price,
          volume: 1,
        }
      } else if (barTime > bar.time) {
        bar = {
          time: barTime,
          open: price,
          high: price,
          low: price,
          close: price,
          volume: 1,
        }
      } else if (barTime === bar.time) {
        bar = {
          ...bar,
          high: Math.max(bar.high, price),
          low: Math.min(bar.low, price),
          close: price,
          volume: bar.volume + 1,
        }
      } else {
        console.warn('[dxtradeCandleStream] skip out-of-order tick', {
          symbol: sub.symbol,
          resolution: sub.resolution,
          tickTimestamp: tick.Timestamp,
          tickIso: new Date(ts).toISOString(),
          barTimeIso: new Date(barTime).toISOString(),
          lastBarIso: new Date(bar.time).toISOString(),
        })
        return
      }

      this.lastBars.set(key, bar)
      sub.onRealtimeCallback(bar)
    })
  }
  handleMessage(message: any) {
    if (message.$type !== 'TicksMarketData') return
    message.Ticks.forEach((tick: Tick) => this.handleTick(tick))
  }
}

export const dxtradeCandleStream = new DxtradeCandleStream()

function parseTickTimestamp(timestamp: string): number {
  if (!timestamp) return NaN

  if (/[zZ]$|[+-]\d{2}:\d{2}$/.test(timestamp)) {
    return new Date(timestamp).getTime()
  }

  return new Date(`${timestamp}Z`).getTime()
}

function getBarStartTime(ts: number, resolution: string) {
  if (resolution === 'D' || resolution === '1D') {
    const d = new Date(ts)
    d.setUTCHours(0, 0, 0, 0)
    return d.getTime()
  }

  if (resolution === 'W' || resolution === '1W') {
    const d = new Date(ts)
    const day = d.getUTCDay() || 7
    d.setUTCDate(d.getUTCDate() - day + 1)
    d.setUTCHours(0, 0, 0, 0)
    return d.getTime()
  }

  if (resolution === 'M' || resolution === '1M') {
    const d = new Date(ts)
    d.setUTCDate(1)
    d.setUTCHours(0, 0, 0, 0)
    return d.getTime()
  }

  const minutes = parseInt(resolution, 10)
  if (!Number.isFinite(minutes) || minutes <= 0) {
    throw new Error(`Unsupported resolution: ${resolution}`)
  }

  const intervalMs = minutes * 60 * 1000
  return Math.floor(ts / intervalMs) * intervalMs
}
